import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowUp, LayoutList, Rows3, Wand2 } from 'lucide-react'
import Layout from '../../components/Layout'
import BracketMatchList from '../../components/admin/BracketMatchList'
import BracketOutlineEditor from '../../components/admin/BracketOutlineEditor'
import BracketQuickSetup from '../../components/admin/BracketQuickSetup'
import BracketStickyHeader from '../../components/admin/BracketStickyHeader'
import PoolPlaySetup from '../../components/admin/PoolPlaySetup'
import { Button } from '../../components/ui/button'
import useBracketPopulation from '../../hooks/useBracketPopulation'

const VIEW_STORAGE_KEY = 'dub.bracketEditor.view'
const VIEWS = [
  { id: 'list', label: 'List', icon: LayoutList },
  { id: 'outline', label: 'Outline', icon: Rows3 },
  { id: 'quick', label: 'Quick Setup', icon: Wand2 },
]

function isValidView(v) {
  return VIEWS.some((view) => view.id === v)
}

export default function BracketPopulation() {
  const { bracketId } = useParams()
  const navigate = useNavigate()

  const [view, setView] = useState(() => {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY)
      return isValidView(stored) ? stored : 'list'
    } catch {
      return 'list'
    }
  })

  // DUB-54: surface a back-to-top button on the (often very tall) seed
  // editor, matching the affordance that used to exist in the previous app.
  const [showBackToTop, setShowBackToTop] = useState(false)
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 300)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view)
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }, [view])

  const {
    bracket,
    tournament,
    tournamentBrackets,
    loading,
    saving,
    matches,
    isHeaderSticky,
    displayValidationErrors,
    matchRefs,
    stickyHeaderRef,
    handleAddMatch,
    handleInsertMatch,
    handleAddPQMatch,
    handleDeleteMatch,
    handleUpdateMatch,
    handleReplaceMatches,
    handlePlayer1TeamChange,
    handlePlayer2TeamChange,
    handleSave,
    handleFocusCapture,
    handleBlurCapture,
    getUsedPlayerIds,
  } = useBracketPopulation(bracketId)

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading bracket...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!bracket) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground mb-4">Bracket not found</p>
            <Button variant="primaryNeon" onClick={() => navigate('/tournaments')}>
              Back to Tournaments
            </Button>
          </div>
        </div>
      </Layout>
    )
  }

  // Pool-play brackets get a different setup surface; the existing
  // single-elim editor (matches list / outline / quick setup) doesn't apply.
  if (bracket.format === 'pool_then_sunday') {
    return (
      <Layout>
        <PoolPlaySetup bracketId={bracketId} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-96">
        <BracketStickyHeader
          stickyHeaderRef={stickyHeaderRef}
          isHeaderSticky={isHeaderSticky}
          tournament={tournament}
          bracket={bracket}
          bracketId={bracketId}
          tournamentBrackets={tournamentBrackets}
          saving={saving}
          onSave={handleSave}
        />

        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div
            role="radiogroup"
            aria-label="Bracket editor view"
            className="inline-flex rounded-md border bg-muted/30 p-0.5"
          >
            {VIEWS.map((v) => {
              const Icon = v.icon
              const active = view === v.id
              return (
                <button
                  key={v.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setView(v.id)}
                  className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    active
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{v.label}</span>
                </button>
              )
            })}
          </div>
          {view !== 'list' && (
            <span className="text-xs text-muted-foreground">
              Preview view — save still uses the same workflow.
            </span>
          )}
        </div>

        <div>
          {view === 'list' && (
            <BracketMatchList
              matches={matches}
              bracket={bracket}
              matchRefs={matchRefs}
              displayValidationErrors={displayValidationErrors}
              onAddMatch={handleAddMatch}
              onInsertMatch={handleInsertMatch}
              onAddPQMatch={handleAddPQMatch}
              onDeleteMatch={handleDeleteMatch}
              onUpdateMatch={handleUpdateMatch}
              onPlayer1TeamChange={handlePlayer1TeamChange}
              onPlayer2TeamChange={handlePlayer2TeamChange}
              onFocusCapture={handleFocusCapture}
              onBlurCapture={handleBlurCapture}
              getUsedPlayerIds={getUsedPlayerIds}
            />
          )}

          {view === 'outline' && (
            <BracketOutlineEditor
              matches={matches}
              bracket={bracket}
              matchRefs={matchRefs}
              displayValidationErrors={displayValidationErrors}
              onAddMatch={handleAddMatch}
              onInsertMatch={handleInsertMatch}
              onAddPQMatch={handleAddPQMatch}
              onDeleteMatch={handleDeleteMatch}
              onUpdateMatch={handleUpdateMatch}
              onPlayer1TeamChange={handlePlayer1TeamChange}
              onPlayer2TeamChange={handlePlayer2TeamChange}
              onFocusCapture={handleFocusCapture}
              onBlurCapture={handleBlurCapture}
              getUsedPlayerIds={getUsedPlayerIds}
            />
          )}

          {view === 'quick' && (
            <BracketQuickSetup
              matches={matches}
              bracket={bracket}
              onReplaceMatches={handleReplaceMatches}
            />
          )}
        </div>
      </div>

      {/* DUB-54 — floating back-to-top arrow */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`btn-primary-neon fixed bottom-20 right-5 sm:bottom-6 sm:right-6 z-40 h-11 w-11 !p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shadow-green-md-inset transition-[opacity,transform] duration-300 ease-out ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5 mx-auto" />
      </button>
    </Layout>
  )
}
